import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_LOCAL_URL } from '../../../../config';

const emptyProcessingAid = {
  processingAidRmCode: "",
  processingAidCommonName: "",
  processingAidScientificName: "",
  processingAidStatus: "",
  processingAidPlantPartUsed: "",
  processingAidRole: "",
  processingAidForm: "",
  processingAidGreenChemistry: "",
};

const emptyIngredient = {
  ingredientCode: "",
  commonName: "",
  scientificName: "",
  rmCode: "",
  organicStatus: "",
  scope: "",
  coordinates: "",
  plantPartUsed: "",
  organicControlPoints: "",
  sterilization: "",
  extractionType: "",
  standardizedFor: "",
  criticalQuantificationControl: "",
  identity: "",
  strain: "",
  wetDry: "",

  processingAids: [{ ...emptyProcessingAid }],
};

const ingredientFields = [
  { key: "ingredientCode", label: "Ingredient Code", required: true },
  { key: "commonName", label: "Common Name", required: true },
  { key: "scientificName", label: "Scientific Name", required: true },
  { key: "rmCode", label: "RM Code" },
  { key: "organicStatus", label: "Organic Status" },
  { key: "scope", label: "Scope" },
  { key: "coordinates", label: "Coordinates" },
  { key: "plantPartUsed", label: "Plant Part Used" },
  { key: "organicControlPoints", label: "Organic Control Points" },
  { key: "sterilization", label: "Sterilization" },
  { key: "extractionType", label: "Extraction Type" },
  { key: "standardizedFor", label: "Standardized For" },
  { key: "criticalQuantificationControl", label: "Critical Quantification Control" },
  { key: "identity", label: "Identity" },
  { key: "strain", label: "Strain" },
  { key: "wetDry", label: "Wet/Dry" },
];

const processingAidFields = [
  { key: "processingAidRmCode", label: "Processing Aid RM Code" },
  { key: "processingAidCommonName", label: "Processing Aid Common Name" },
  { key: "processingAidScientificName", label: "Processing Aid Scientific Name" },
  { key: "processingAidStatus", label: "Processing Aid Status" },
  { key: "processingAidPlantPartUsed", label: "Processing Aid Plant Part Used" },
  { key: "processingAidRole", label: "Processing Aid Role" },
  { key: "processingAidForm", label: "Processing Aid Form" },
  { key: "processingAidGreenChemistry", label: "Processing Aid Green Chemistry" },
];

export default function IngredientSpecificationForm({
  data,
  onChange,
  onNext,
  onBack,
  errors = {}, // { [index]: { ingredientCode?, commonName?, scientificName? } }
}) {
  const items = data.ingredients || [];
  const [existingIngredients, setExistingIngredients] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");

const setItems = (arr, meta) => {

  const cloned = arr.map((ingredient) => ({
    ...ingredient,

    processingAids: (ingredient.processingAids || []).map((aid) => ({
      ...aid,
    })),
  }));

  onChange("ingredients", cloned, meta);
};

  const handleChange = (index, key, value) => {
const arr = items.map((item) => ({
  ...item,
  processingAids: [...(item.processingAids || [])],
}));
    arr[index] = { ...arr[index], [key]: value };
    setItems(arr, { clearAt: { index, key } });
  };

 const handleProcessingAidChange = (
  ingredientIndex,
  processingAidIndex,
  key,
  value
) => {
const arr = items.map((item) => ({
  ...item,
  processingAids: [...(item.processingAids || [])],
}));

  arr[ingredientIndex] = {
    ...arr[ingredientIndex],
    processingAids: [
      ...(arr[ingredientIndex].processingAids || [])
    ],
  };

  arr[ingredientIndex].processingAids[processingAidIndex] = {
    ...arr[ingredientIndex].processingAids[processingAidIndex],
    [key]: value,
  };

  setItems(arr);
};

const addProcessingAid = (ingredientIndex) => {
const arr = items.map((item) => ({
  ...item,
  processingAids: [...(item.processingAids || [])],
}));

  arr[ingredientIndex] = {
    ...arr[ingredientIndex],
    processingAids: [
      ...(arr[ingredientIndex].processingAids || []),
      { ...emptyProcessingAid },
    ],
  };

  setItems(arr);
};

const removeProcessingAid = (
  ingredientIndex,
  processingAidIndex
) => {
const arr = items.map((item) => ({
  ...item,
  processingAids: [...(item.processingAids || [])],
}));

  arr[ingredientIndex] = {
    ...arr[ingredientIndex],
    processingAids: (
  arr[ingredientIndex].processingAids || []
).filter(
      (_, idx) => idx !== processingAidIndex
    ),
  };

  setItems(arr);
};

const addItem = () =>
  setItems([
    ...items,
    {
      ...emptyIngredient,
      processingAids: [{ ...emptyProcessingAid }],
    },
  ]);

 const removeItem = (index) => {

  const arr = items.filter(
    (_, idx) => idx !== index
  );

  setItems(arr);
};

  const addExistingIngredient = () => {
    if (!selectedCode) return;

const alreadyExists = items.some(
  (item) => item.ingredientCode === selectedCode
);

if (alreadyExists) {
  alert("Ingredient already added");
  return;
}
    const selected = existingIngredients.find(
      (ing) => ing.ingredientCode === selectedCode
    );
   if (selected) {
  const deepClonedIngredient = JSON.parse(
  JSON.stringify(selected)
);

deepClonedIngredient.processingAids =
  deepClonedIngredient.processingAids?.length > 0
    ? deepClonedIngredient.processingAids
    : [{ ...emptyProcessingAid }];

  setItems([...items, deepClonedIngredient]);

  setSelectedCode("");
}
  };

  // Fetch existing ingredients from backend
 useEffect(() => {

  const token = localStorage.getItem("token");

  axios
    .get(`${API_BASE_LOCAL_URL}/api/ingredients`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => setExistingIngredients(res.data))
    .catch((err) =>
      console.error("Failed to fetch ingredients", err)
    );

}, []);

  return (
    <div>
      <h4 className="text-success">Ingredient Specification</h4>

      {/* Select Existing Ingredient */}
      <div className="mt-3 mb-3">
        <label className="form-label">Select Existing Ingredient</label>
        <div className="d-flex">
          <select
            className="form-select me-2"
            value={selectedCode}
            onChange={(e) => setSelectedCode(e.target.value)}
          >
            <option value="">-- Choose Ingredient --</option>
            {existingIngredients.map((ing) => (
              <option key={ing.ingredientCode} value={ing.ingredientCode}>
                {ing.commonName} ({ing.ingredientCode})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-success"
            onClick={addExistingIngredient}
          >
            Add
          </button>
        </div>
      </div>

      {items.length === 0 && (
        <p className="text-muted">No ingredients added yet. Use the options above to add.</p>
      )}

      {items.map((item, i) => {
        const rowErrors = errors[i] || {};
        return (
          <div key={`ingredient-${i}`} className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Ingredient #{i + 1}</strong>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => removeItem(i)}
              >
                Remove
              </button>
            </div>

            <div className="row">
              {ingredientFields.map(({ key, label, required }) => (
                <div className="col-md-4 mb-2" key={key}>
                  <input
                    className={`form-control ${rowErrors[key] ? "is-invalid" : ""}`}
                    placeholder={label + (required ? " *" : "")}
                    value={item[key] ?? ""}
                    onChange={(e) => handleChange(i, key, e.target.value)}
                  />
                  {rowErrors[key] && (
                    <div className="invalid-feedback">{rowErrors[key]}</div>
                  )}
                </div>
              ))}
            </div>

           <h6 className="mt-3">Processing Aids</h6>

{(item.processingAids || []).map((aid, aidIndex) => (
  <div
    key={`aid-${i}-{aidIndex}`}
    className="border rounded p-3 mb-3 bg-light"
  >
    <div className="d-flex justify-content-between align-items-center mb-2">
      <strong>Processing Aid #{aidIndex + 1}</strong>

     <button
  type="button"
  className="btn btn-danger btn-sm"
  disabled={item.processingAids?.length === 1}
  onClick={() => removeProcessingAid(i, aidIndex)}
>
  Remove
</button>
    </div>

    <div className="row">
      {processingAidFields.map(({ key, label }) => (
        <div className="col-md-4 mb-2" key={key}>
          <input
            className="form-control"
            placeholder={label}
            value={aid[key] ?? ""}
            onChange={(e) =>
              handleProcessingAidChange(
                i,
                aidIndex,
                key,
                e.target.value
              )
            }
          />
        </div>
      ))}
    </div>
  </div>
))}

<button
  type="button"
  className="btn btn-outline-success btn-sm"
  onClick={() => addProcessingAid(i)}
>
  Add Processing Aid
</button>
          </div>
        );
      })}

      {/* Add New Ingredient Button */}
      <button type="button" className="btn btn-light mt-2" onClick={addItem}>
        Add New Ingredient
      </button>

      <div className="d-flex justify-content-between mt-3">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn btn-primary" onClick={onNext}>
          Next
        </button>
      </div>
    </div>
  );
}
